import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, KeyRound, Monitor, Moon, ShieldCheck, Sun } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  PageHeader,
  Skeleton,
} from '@/components/ui';

const SECTIONS = [
  { id: 'security', label: 'Security', icon: ShieldCheck },
  { id: 'appearance', label: 'Appearance', icon: Monitor },
] as const;

export default function Settings() {
  const [section, setSection] = useState<(typeof SECTIONS)[number]['id']>('security');

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow="Your account"
        title="Settings"
        description="How your account is protected, and how the portal looks."
      />

      <div className="grid gap-8 lg:grid-cols-[200px_1fr]">
        <nav className="flex gap-1.5 overflow-x-auto lg:flex-col lg:overflow-visible">
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setSection(id)}
              className={cn(
                'flex shrink-0 items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors',
                section === id
                  ? 'bg-primary text-primary-foreground shadow-brand'
                  : 'text-foreground-secondary hover:bg-accent hover:text-accent-foreground',
              )}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </nav>

        <div className="min-w-0">
          {section === 'security' && <SecuritySettings />}
          {section === 'appearance' && <AppearanceSettings />}
        </div>
      </div>
    </div>
  );
}

function SecuritySettings() {
  const [form, setForm] = useState({ currentPassword: '', password: '', confirmPassword: '' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: profile, isLoading } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => api.get<{ twoFactorEnabled: boolean; email: string }>('/members/me'),
  });

  const change = useMutation({
    mutationFn: () => api.post<{ message: string }>('/auth/change-password', form),
    onSuccess: (result) => {
      toast.success(result.message);
      setForm({ currentPassword: '', password: '', confirmPassword: '' });
      setFieldErrors({});
      // The server revokes every session on a password change, so the next
      // request would 401 anyway. Say so rather than letting it look broken.
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    },
    onError: (error) => {
      if (error instanceof ApiError) setFieldErrors(error.fieldErrors);
      toast.error(error instanceof Error ? error.message : 'Could not change your password.');
    },
  });

  const strength = passwordStrength(form.password);

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                  profile?.twoFactorEnabled
                    ? 'bg-success-soft text-success'
                    : 'bg-warning-soft text-warning',
                )}
              >
                <ShieldCheck size={16} />
              </span>
              <div>
                <h2 className="font-heading font-bold text-foreground">
                  Two-factor authentication
                </h2>
                <p className="mt-1 max-w-md text-sm leading-relaxed text-foreground-secondary">
                  {profile?.twoFactorEnabled
                    ? 'Enabled. A code from your authenticator app is required each time you sign in.'
                    : 'Not enabled. Adding a second factor means a stolen password alone cannot reach your loans.'}
                </p>
              </div>
            </div>
            <span
              className={cn(
                'shrink-0 rounded-full px-3 py-1 text-xs font-bold ring-1 ring-inset',
                profile?.twoFactorEnabled
                  ? 'bg-success-soft text-success-soft-foreground ring-success/20'
                  : 'bg-neutral-soft text-neutral-soft-foreground ring-neutral/20',
              )}
            >
              {profile?.twoFactorEnabled ? 'On' : 'Off'}
            </span>
          </div>

          {!profile?.twoFactorEnabled && (
            <Button
              className="mt-5"
              variant="outline"
              onClick={() => (window.location.href = '/two-factor-setup')}
            >
              <ShieldCheck size={16} /> Set up two-factor authentication
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-5 p-6">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground-secondary">
              <KeyRound size={16} />
            </span>
            <div>
              <h2 className="font-heading font-bold text-foreground">Change password</h2>
              <p className="mt-1 text-sm text-foreground-secondary">
                You will be signed out everywhere, including any device you no longer have.
              </p>
            </div>
          </div>

          <Field
            label="Current password"
            htmlFor="currentPassword"
            required
            error={fieldErrors['currentPassword']}
          >
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={(e) => setForm((f) => ({ ...f, currentPassword: e.target.value }))}
            />
          </Field>

          <Field label="New password" htmlFor="newPassword" required error={fieldErrors['password']}>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          </Field>

          {form.password && (
            <div>
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={cn(
                      'h-1.5 flex-1 rounded-full transition-colors',
                      index < strength.score ? strength.colour : 'bg-muted',
                    )}
                  />
                ))}
              </div>
              <p className="mt-1.5 text-xs text-foreground-secondary">{strength.label}</p>
            </div>
          )}

          <Field
            label="Confirm new password"
            htmlFor="confirmNew"
            required
            error={fieldErrors['confirmPassword']}
          >
            <Input
              id="confirmNew"
              type="password"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
            />
          </Field>

          <Button
            loading={change.isPending}
            disabled={!form.currentPassword || strength.score < 3}
            onClick={() => change.mutate()}
          >
            Change password
          </Button>
        </CardContent>
      </Card>

      <Alert tone="info" title="Your data">
        UNIFEMGA holds your identity documents and financial records under the Nigeria Data
        Protection Act. To request a copy of everything held about you, or to ask for a correction,
        email{' '}
        <a href="mailto:nunifemga@gmail.com" className="font-bold underline">
          nunifemga@gmail.com
        </a>
        .
      </Alert>
    </div>
  );
}

function AppearanceSettings() {
  // "System" is stored explicitly rather than as the absence of the key.
  // Absence now means light — the default for anyone who has never chosen —
  // so the two states have to be distinguishable.
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    const stored = localStorage.getItem('unifemga-theme');
    return stored === 'dark' || stored === 'system' ? stored : 'light';
  });

  const apply = (next: 'light' | 'dark' | 'system') => {
    setTheme(next);
    localStorage.setItem('unifemga-theme', next);
    document.documentElement.classList.toggle(
      'dark',
      next === 'dark' ||
        (next === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches),
    );
  };

  const options = [
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'dark' as const, label: 'Dark', icon: Moon },
    { value: 'system' as const, label: 'Match device', icon: Monitor },
  ];

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="font-heading text-lg font-bold text-foreground">Appearance</h2>
        <p className="mt-1 text-sm text-foreground-secondary">Applies to this browser only.</p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {options.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => apply(value)}
              className={cn(
                'flex flex-col items-center gap-2.5 rounded-xl border p-5 transition-all',
                theme === value
                  ? 'border-primary bg-primary/5 shadow-card'
                  : 'border-border hover:border-primary/30 hover:bg-accent',
              )}
            >
              <Icon
                size={20}
                className={theme === value ? 'text-primary' : 'text-foreground-secondary'}
              />
              <span className="text-sm font-semibold text-foreground">{label}</span>
              {theme === value && <Check size={13} className="text-primary" />}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** Rough strength meter. Mirrors the server rule: 12+ chars, mixed case, a digit. */
function passwordStrength(password: string): { score: number; label: string; colour: string } {
  if (!password) return { score: 0, label: '', colour: 'bg-muted' };

  let score = 0;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (password.length >= 16 || /[^A-Za-z0-9]/.test(password)) score += 1;

  const labels = [
    'Too short',
    'Weak — needs at least 12 characters',
    'Needs upper case, lower case and a number',
    'Good',
    'Strong',
  ];
  const colours = ['bg-muted', 'bg-danger', 'bg-warning', 'bg-success', 'bg-success'];

  return { score, label: labels[score] ?? '', colour: colours[score] ?? 'bg-muted' };
}
